import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Lightning } from "@phosphor-icons/react";

export default function Workflows() {
  const { user } = useAuth();
  const { isSuper, activeTenant, activeName } = useTenant();
  const [rules, setRules] = useState([]);
  const canEdit = ["super_admin", "client_admin"].includes(user.role);
  const needsTenant = isSuper && !activeTenant;

  const load = () => {
    if (needsTenant) { setRules([]); return; }
    api.get("/workflows").then((r) => setRules(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [activeTenant]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (r) => {
    await api.patch(`/workflows/${r.id}`, { active: !r.active });
    load();
  };

  return (
    <div data-testid="workflows-page">
      <PageHeader title="Automation Workflows" subtitle={`DPD-triggered rule engine${isSuper && activeName ? ` — ${activeName}` : ""}`}>
        {canEdit && !needsTenant && <RuleDialog onDone={load} />}
      </PageHeader>
      {needsTenant ? (
        <Empty text="Select a tenant from the switcher in the top bar to view and manage its automation rules." />
      ) : (
        <>
          <div className="space-y-3">
            {rules.map((r) => (
              <div key={r.id} className="border border-border bg-card p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-sm bg-secondary border border-border flex items-center justify-center shrink-0">
                    <Lightning size={18} weight="bold" className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-sm text-muted-foreground mono">
                      WHEN {r.trigger_type} {r.operator} {r.value} → {r.action_type.replace(/_/g, " ")} ({r.action_channel})
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="label-caps">P{r.priority}</span>
                  {canEdit && <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`toggle-rule-${r.id}`} />}
                  <span className={`text-xs font-semibold ${r.active ? "text-emerald-600" : "text-muted-foreground"}`}>{r.active ? "Active" : "Paused"}</span>
                </div>
              </div>
            ))}
          </div>
          {rules.length === 0 && <Empty text="No automation rules configured" />}
        </>
      )}
    </div>
  );
}

function RuleDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", trigger_type: "dpd", operator: ">=", value: "30", action_type: "send_message", action_channel: "whatsapp", priority: "1" });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      await api.post("/workflows", { ...f, priority: +f.priority });
      toast.success("Rule created");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm gap-2" data-testid="add-rule-button"><Plus size={16} weight="bold" /> New Rule</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle>New Automation Rule</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Rule Name</Label><Input className="rounded-sm" value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="rule-name" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Trigger</Label>
              <Select value={f.trigger_type} onValueChange={(v) => upd("trigger_type", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["dpd", "status", "bucket"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Operator</Label>
              <Select value={f.operator} onValueChange={(v) => upd("operator", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{[">=", ">", "=", "<", "<="].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Value</Label><Input className="rounded-sm" value={f.value} onChange={(e) => upd("value", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Action</Label>
              <Select value={f.action_type} onValueChange={(v) => upd("action_type", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["send_message", "create_task", "escalate", "mark_settlement_eligible"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Channel</Label>
              <Select value={f.action_channel} onValueChange={(v) => upd("action_channel", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["whatsapp", "sms", "email"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Priority</Label><Input type="number" className="rounded-sm" value={f.priority} onChange={(e) => upd("priority", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm" data-testid="save-rule-button">Create Rule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
